import { importSelectedDataByAlice } from "../../../../API/AliceGPT/AliceGPT";
import { callApiWithAuthorization } from "../../../../API/CallApi";
import { convertTypeToDesignerType } from "../../../Helpers/Recommendation/AliceGPT";
import Color from "../../../Helpers/color";
import { icons } from "../../../Shared/IconPicker/IconPicker";
import { CreateProjectRequest } from "./RequestTypes";

/**
 * Function to create project and publish it
 * @param {CreateProjectRequest} createProjectRequest body of the request
 * @returns create project response
 */
export async function createProject(
    createProjectRequest
) {
    if (!(createProjectRequest instanceof CreateProjectRequest)) {
        throw new Error("Invalid request type");
    }
    const { projectName, domain, description, language } = createProjectRequest.projectProperties;
    const { email } = createProjectRequest.userProperties;
    const { organizationId, organizationName } = createProjectRequest.organizationDetails;

    const properties = {
        email,
        projectName: projectName.trim().replace(/\s\s+/g, " "),
        projectUrl: projectName.trim().replace(/\s\s+/g, " ").replace(/[^a-zA-Z0-9\~\_\-\.]/g, ""),
        domain,
        description,
        Icon: getRandomIcon(),
        developerMode: false,
        abbreviation: "",
        bgColor: Color(),
        tag: "",
        hasSchemaChanges: false,
        isPublished: false,
        isSuperApp: false,
        isFeaturedApp: false,
        TilesImages: "",
        SlideshowImages: "",
        language
    };

    const request = await callApiWithAuthorization("/api/v0/project/create-and-publish?language=" + language, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            organizationId,
            organizationName,
            properties,
            entities: createProjectRequest.entities,
            relations: createProjectRequest.relations,
        }),
    });
    const response = await request.json();

    return response;
}

export async function createProjectUsingAI(
    createProjectRequest
) {
    if (!(createProjectRequest instanceof CreateProjectRequest)) {
        throw new Error("Invalid request type");
    }
    const { projectName, domain, description, language } = createProjectRequest.projectProperties;
    const { email } = createProjectRequest.userProperties;
    const { organizationId, organizationName } = createProjectRequest.organizationDetails;

    const properties = {
        email,
        projectName: projectName.trim().replace(/\s\s+/g, " "),
        projectUrl: projectName.trim().replace(/\s\s+/g, " ").replace(/[^a-zA-Z0-9\~\_\-\.]/g, ""),
        domain,
        description,
        Icon: getRandomIcon(),
        developerMode: false,
        abbreviation: "",
        bgColor: Color(),
        tag: "",
        hasSchemaChanges: false,
        isPublished: false,
        isSuperApp: false,
        isFeaturedApp: false,
        TilesImages: "",
        SlideshowImages: "",
        language
    };

    const request = await callApiWithAuthorization("/api/v0/project/create-and-publish-using-ai?language=" + language, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            organizationId,
            organizationName,
            properties,
            entities: createProjectRequest.entities,
            relations: createProjectRequest.relations,
        }),
    });
    const response = await request.json();

    return response;
}

export async function createOnlyProject(createProjectRequest) {
    if (!(createProjectRequest instanceof CreateProjectRequest)) {
        throw new Error("Invalid request type");
    }

    const { projectName, domain, description } = createProjectRequest.projectProperties;
    const { email } = createProjectRequest.userProperties;
    const { organizationId, organizationName } = createProjectRequest.organizationDetails;

    const properties = {
        organizationId,
        organizationName,
        email,
        projectName: projectName.trim().replace(/\s\s+/g, " "),
        projectUrl: projectName.trim().replace(/\s\s+/g, " ").replace(/[^a-zA-Z0-9\~\_\-\.]/g, ""),
        domain,
        description,
        Icon: getRandomIcon(),
        developerMode: false,
        abbreviation: "",
        bgColor: Color(),
        tag: "",
        hasSchemaChanges: false,
        isPublished: false,
        isSuperApp: false,
        isFeaturedApp: false,
        TilesImages: "",
        SlideshowImages: "",
    };

    const request = await callApiWithAuthorization("/api/v0/createProjectandRole", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(properties),
    });
    const response = await request.json();

    return response;
}

function getRandomIcon() {
    return icons[Math.floor(Math.random() * icons.length)];
}

export async function addSelectedDataToProject(organizationId, projectId, entities, relations) {
    const entitiesAndAttributes = entities.map((entity) => ({
        name: entity.name,
        icon: entity.icon,
        isUserEntity: entity.isUserEntity,
        attributes: entity.attributes.map((attribute) => ({
            name: attribute.name,
            type: attribute.type,
            picklist: attribute.picklist,
            required: attribute.required,
            unique: attribute.unique,
            isAzureAttribute: attribute.isAzureAttribute
        }))
    }));
    const response = await importSelectedDataByAlice(organizationId, projectId, entitiesAndAttributes, relations);
    return response;
}

export function getEntitiesExceedingAttributeLimitOnCreatingRelations(entities, relations, subscription) {
    const toEntitiesAttributeMap = relations.map(relation => (
        { name: relation.to, showReferenceAttribute: relation.showReferenceAttribute }
    )).reduce((acc, relationInfo) => {
        if (!acc[relationInfo.name]) {
            const entity = entities.find(entity => entity.name === relationInfo.name);
            const selectedAttributes = entity.attributes.filter(attribute => attribute.selected);
            acc[relationInfo.name] = selectedAttributes.length;
            if (relationInfo.showReferenceAttribute) {
                acc[relationInfo.name]++;
            }
        } else if (relationInfo.showReferenceAttribute) {
            acc[relationInfo.name]++;
        }
        return acc;
    }, {});
    const entitiesWithAttributeLimitExceeded = Object.keys(toEntitiesAttributeMap).filter(entityName => {
        return toEntitiesAttributeMap[entityName] > subscription.maxAttributesPerApp;
    });
    return entitiesWithAttributeLimitExceeded;
}